import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { useState } from "react";

import { captureException } from "util/exception";
import { useApolloClient, useMutation } from "util/graphql";
import { useActiveOrganization } from "common/account/active_organization";
import AlertMessage from "common/core/alert_message";
import Button from "common/core/button";

import { type PlaidInfo, verifyAch } from "../ach_util";
import Styles from "./ach.module.scss";
import Tooltip from "../../../core/tooltip";
import Icon from "../../../core/icon";
import AddAchAccountMutation from "../graphql/add_ach_account_to_organization_mutation.graphql";
import { Heading, Paragraph } from "../../../core/typography";
import type { PaymentSource } from "./inline_form";

const MESSAGES = defineMessages({
  change: {
    id: "1ab24df3-d2b3-4cac-bee6-ab9925a4abf3",
    defaultMessage: "Change bank account",
  },
  connectAccount: {
    id: "8287a086-a613-4b32-a882-77bcb7fefcac",
    defaultMessage: "Connect bank account",
  },
  achPayDetails: {
    id: "db73bcf1-bb1b-4275-b2e3-689069bd1d1f",
    defaultMessage:
      "Allow your company to pay directly with a bank account. These charges will be batched and processed daily. You will be taken to Plaid to securely connect your account.",
  },
});

function ConnectedAccount({
  existingAccount,
  newAccount,
}: {
  existingAccount: PaymentSource | null;
  newAccount: PlaidInfo | null;
}) {
  const accountToUseName = newAccount?.accountName || existingAccount?.name;

  if (!accountToUseName) {
    return null;
  }

  return (
    <div className="PaymentModalDetails--connected-bank">
      <Heading level="h2" textColor="subtle" textStyle="allCapsLabelSmall">
        <FormattedMessage
          id="2614d7e9-7ac5-414c-add0-aef3a31f1eb6"
          defaultMessage="Account connected"
        />
      </Heading>
      <Paragraph className="PaymentModalDetails--connected-bank-name">
        <strong>{`[${accountToUseName}]`}</strong>
      </Paragraph>
    </div>
  );
}

export function AchForm({
  paymentSource,
  tooltipDescription,
  organizationId,
}: {
  paymentSource: PaymentSource | null;
  tooltipDescription: boolean;
  organizationId: string;
}) {
  const intl = useIntl();
  const client = useApolloClient();
  const [activeOrganizationId] = useActiveOrganization();
  const [verifying, setVerifying] = useState(false);
  const [plaidError, setPlaidError] = useState(false);
  const [newAccount, setNewAccount] = useState<PlaidInfo | null>(null);
  const addAchAccountMutateFn = useMutation(AddAchAccountMutation);

  const onAchChange = async (plaidInfo: PlaidInfo) => {
    //This mutation will automatically update the default payer to ORGANIZATION_ACH
    try {
      await addAchAccountMutateFn({
        variables: {
          input: {
            organizationId,
            accountId: plaidInfo.accountId,
            publicToken: plaidInfo.token,
          },
        },
      });
      setNewAccount(plaidInfo);
    } catch (e) {
      captureException(e);
      setPlaidError(true);
    }
  };

  const connectBankAccount = async () => {
    setPlaidError(false);
    setVerifying(true);
    try {
      await verifyAch(client, activeOrganizationId!, onAchChange);
    } catch {
      setPlaidError(true);
    } finally {
      setVerifying(false);
    }
  };
  const existingAccount = paymentSource?.type === "AchAccount" ? paymentSource : null;

  return (
    <div>
      {!tooltipDescription && (
        <div className={Styles.achPayDetails}>{intl.formatMessage(MESSAGES.achPayDetails)}</div>
      )}

      <ConnectedAccount existingAccount={existingAccount} newAccount={newAccount} />

      <Button
        buttonColor="action"
        variant="secondary"
        onClick={connectBankAccount}
        isLoading={verifying}
        className={Styles.connectAccountButton}
      >
        {existingAccount
          ? intl.formatMessage(MESSAGES.change)
          : intl.formatMessage(MESSAGES.connectAccount)}
      </Button>

      {tooltipDescription && (
        <Tooltip
          target={<Icon name="question-circle" />}
          placement="top"
          containerClassName={Styles.tooltip}
        >
          {intl.formatMessage(MESSAGES.achPayDetails)}
        </Tooltip>
      )}

      {plaidError && (
        <AlertMessage kind="danger">
          <FormattedMessage
            id="92e7b9ef-4e42-438a-9814-f95e6155aa9d"
            defaultMessage="Sorry, something went wrong. Please try again."
          />
        </AlertMessage>
      )}
    </div>
  );
}
